import { isReadableAtom } from "../util/nanostores.js";
import { hydrateFromWindow } from "../util/ssr.js";
import { isGetHook, isMutatorHook, isStore } from "./client.js";
import { listenKeys } from "nanostores";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";

//#region src/client/react.ts
function createReactHook(hook) {
	return ({ path, query } = {}) => {
		const pathParamValues = path ? Object.values(path) : [];
		const queryParamValues = query ? Object.values(query) : [];
		const store = useMemo(() => hook.store({
			path,
			query
		}), [hook, ...[...pathParamValues, ...queryParamValues]]);
		if (typeof window === "undefined") return store.get();
		return useStore(store);
	};
}
function createReactMutator(hook) {
	return () => {
		return useStore(useMemo(() => hook.mutatorStore, [hook]));
	};
}
const isPlainObject = (value) => {
	if (!value || typeof value !== "object") return false;
	const prototype = Object.getPrototypeOf(value);
	return prototype === Object.prototype || prototype === null;
};
const areStoreFactoryValuesEqual = (left, right) => {
	if (Object.is(left, right)) return true;
	if (Array.isArray(left) && Array.isArray(right)) return left.length === right.length && left.every((value, index) => areStoreFactoryValuesEqual(value, right[index]));
	if (left instanceof Date && right instanceof Date) return left.getTime() === right.getTime();
	if (isReadableAtom(left) || isReadableAtom(right)) return left === right;
	if (typeof left === "function" || typeof right === "function") return left === right;
	if (isPlainObject(left) && isPlainObject(right)) {
		const leftKeys = Object.keys(left).sort();
		const rightKeys = Object.keys(right).sort();
		return leftKeys.length === rightKeys.length && leftKeys.every((key, index) => key === rightKeys[index] && areStoreFactoryValuesEqual(left[key], right[key]));
	}
	return false;
};
const areStoreFactoryArgsEqual = (left, right) => left.length === right.length && left.every((value, index) => areStoreFactoryValuesEqual(value, right[index]));
const getStoreDisposer = (value) => {
	const disposer = value[Symbol.dispose];
	return typeof disposer === "function" ? disposer.bind(value) : void 0;
};
const createReactStoreObjectView = (value, getAtomValue) => {
	const atomValues = /* @__PURE__ */ new Map();
	const boundMethods = /* @__PURE__ */ new Map();
	return new Proxy(value, { get(target, property, _receiver) {
		const propertyValue = Reflect.get(target, property, target);
		if (isReadableAtom(propertyValue)) {
			if (atomValues.has(propertyValue)) return atomValues.get(propertyValue);
			const atomValue = getAtomValue(propertyValue);
			atomValues.set(propertyValue, atomValue);
			return atomValue;
		}
		if (typeof propertyValue === "function") {
			if (boundMethods.has(property)) return boundMethods.get(property);
			const boundMethod = propertyValue.bind(target);
			boundMethods.set(property, boundMethod);
			return boundMethod;
		}
		return propertyValue;
	} });
};
function unwrapReactStoreValueOnServer(value) {
	if (isReadableAtom(value)) return value.get();
	return createReactStoreObjectView(value, (store) => store.get());
}
function unwrapReactStoreValue(value) {
	if (isReadableAtom(value)) return useStore(value);
	const atomEntries = Object.keys(value).flatMap((key) => {
		const fieldValue = value[key];
		return isReadableAtom(fieldValue) ? [[key, fieldValue]] : [];
	});
	const snapshotRef = useRef(atomEntries.map(([, store]) => store.get()));
	const getSnapshot = () => {
		const nextSnapshot = atomEntries.map(([, store]) => store.get());
		const previousSnapshot = snapshotRef.current;
		if (previousSnapshot.length === nextSnapshot.length && previousSnapshot.every((entry, index) => Object.is(entry, nextSnapshot[index]))) return previousSnapshot;
		snapshotRef.current = nextSnapshot;
		return nextSnapshot;
	};
	const atomValues = useSyncExternalStore((onStoreChange) => {
		const unsubscribes = atomEntries.map(([, store]) => store.listen(onStoreChange));
		return () => {
			for (const unsubscribe of unsubscribes) unsubscribe();
		};
	}, getSnapshot, getSnapshot);
	return useMemo(() => createReactStoreObjectView(value, (store) => {
		const atomIndex = atomEntries.findIndex(([, entryStore]) => entryStore === store);
		return atomIndex === -1 ? store.get() : atomValues[atomIndex];
	}), [
		value,
		atomEntries,
		atomValues
	]);
}
function createReactStore(hook) {
	return ((...args) => {
		const stableArgsRef = useRef(args);
		const pendingDisposalsRef = useRef(/* @__PURE__ */ new Map());
		if (!areStoreFactoryArgsEqual(stableArgsRef.current, args)) stableArgsRef.current = args;
		const value = useMemo(() => {
			if ("factory" in hook) return hook.factory(...stableArgsRef.current);
			return hook.obj;
		}, [hook, stableArgsRef.current]);
		useEffect(() => {
			const disposer = getStoreDisposer(value);
			const pendingTimeout = pendingDisposalsRef.current.get(value);
			if (pendingTimeout !== void 0) {
				clearTimeout(pendingTimeout);
				pendingDisposalsRef.current.delete(value);
			}
			return () => {
				if (!disposer) return;
				const timeoutId = setTimeout(() => {
					pendingDisposalsRef.current.delete(value);
					disposer();
				}, 0);
				pendingDisposalsRef.current.set(value, timeoutId);
			};
		}, [value]);
		if (typeof window === "undefined") return unwrapReactStoreValueOnServer(value);
		return unwrapReactStoreValue(value);
	});
}
function useFragno(clientObj) {
	const result = {};
	for (const key in clientObj) {
		if (!Object.prototype.hasOwnProperty.call(clientObj, key)) continue;
		const hook = clientObj[key];
		if (isGetHook(hook)) result[key] = createReactHook(hook);
		else if (isMutatorHook(hook)) result[key] = createReactMutator(hook);
		else if (isStore(hook)) result[key] = createReactStore(hook);
		else result[key] = hook;
	}
	return result;
}
function useStore(store, options = {}) {
	const snapshotRef = useRef(store.get());
	const { keys, deps = [store, keys] } = options;
	const subscribe = useCallback((onChange) => {
		const emitChange = (value) => {
			if (snapshotRef.current === value) return;
			snapshotRef.current = value;
			onChange();
		};
		emitChange(store.value);
		if (keys?.length) return listenKeys(store, keys, emitChange);
		return store.listen(emitChange);
	}, deps);
	const get = () => snapshotRef.current;
	return useSyncExternalStore(subscribe, get, () => {
		return get();
	});
}
function FragnoHydrator({ children }) {
	useMemo(() => {
		hydrateFromWindow();
	}, []);
	return children;
}

//#endregion
export { FragnoHydrator, useFragno, useStore };
//# sourceMappingURL=react.js.map